import { AfterViewInit, Component, Input, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { EditorComponent } from '@progress/kendo-angular-editor';
import { imageIcon, hyperlinkOpenIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { DiscussionService, LoggingService, InactivityService } from 'src/app/services';
import { Constants } from 'src/app/shared/models/constants';
@Component({
    selector: 'app-kendo-editor',
    templateUrl: './kendo-editor.component.html',
    styleUrls: ['./kendo-editor.component.scss'],
    standalone: false
})
export class KendoEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('editor') editor!: EditorComponent;

  public imageIcon: SVGIcon = imageIcon;
  public videoIcon: SVGIcon = hyperlinkOpenIcon;

  // Video dialog state
  showVideoDialog = false;
  videoUrl = '';
  videoUrlError = '';

  private _fileDialogOpen = false;

  // Firefox fires a `keydown` Escape event when the OS file dialog is dismissed.
  // Using capture phase (registered in ngAfterViewInit) ensures we intercept it
  // before any bubble-phase listener (e.g. NgbModal) can act on it.
  private readonly _escapeCapture = (event: KeyboardEvent): void => {
    if (this._fileDialogOpen && event.key === 'Escape') {
      event.stopImmediatePropagation();
      this._fileDialogOpen = false;
    }
  };

  @Input() content: any;
  @Input() forumId: number | null = null;
  notes!: string;
  isReadOnlyMode!: boolean;
  showAttachment!: boolean;
  isSubmitButtonEnabled!: boolean;
  isCancelButtonEnabled!: boolean;
  pasteCleanUpSettings: any;
  editorHtmlContent!: string;
  uploadLength: number | null = null;

  constructor(
    private loggingService: LoggingService,
    private inactivityService: InactivityService,
    private discussionService: DiscussionService
  ) {
    this.pasteCleanUpSettings = Constants.PASTE_CLEANUP_SETTINGS;
  }

  ngOnInit(): void {
    if (this.forumId) {
      this.discussionService.getModeratorPermissions(this.forumId)
        .then((result: any) => {
          if (result && result.UploadLength > 0) {
            this.uploadLength = result.UploadLength;
          }
        })
        .catch(() => { /* unable to retrieve upload limit; no size restriction applied */ });
    }
  }

  ngAfterViewInit(): void {
    document.addEventListener('keydown', this._escapeCapture, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this._escapeCapture, true);
  }

  ngOnChanges(changes: SimpleChanges) {
    this.editorHtmlContent = this.content;
  }
  onSubmit() {
    this.loggingService.Info(`START: <MedPoint.WebApp.EMS.Shared.KendoEditorComponent.onSubmit> begins.`);
    this.loggingService.Info(`END: <MedPoint.WebApp.EMS.Shared.KendoEditorComponent.onSubmit> ends.`);
  }

  onCancel() {
    this.loggingService.Info(`START: <MedPoint.WebApp.EMS.Shared.KendoEditorComponent.onCancel> begins.`);
   
    this.loggingService.Info(`END: <MedPoint.WebApp.EMS.Shared.KendoEditorComponent.onCancel> ends.`);
  }

  htmleditorValueChange(value: string): void {
    this.inactivityService.StartWatch();
    if (value) {
      this.isCancelButtonEnabled = true;
      this.isSubmitButtonEnabled = true;
    } else {
      this.isCancelButtonEnabled = false;
      this.isSubmitButtonEnabled = false;
    }
  }

  openVideoDialog(): void {
    this.videoUrl = '';
    this.videoUrlError = '';
    this.showVideoDialog = true;
  }

  closeVideoDialog(): void {
    this.showVideoDialog = false;
    this.videoUrl = '';
    this.videoUrlError = '';
  }

  insertVideo(): void {
    const url = this.videoUrl.trim();
    const embedUrl = this.getEmbedUrl(url);
    if (!embedUrl) {
      this.videoUrlError = 'Please enter a valid YouTube or Vimeo URL.';
      return;
    }
    const iframeHtml =
      `<p><iframe src="${embedUrl}" width="560" height="315" ` +
      `frameborder="0" allowfullscreen ` +
      `allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture">` +
      `</iframe></p>`;
    this.editorHtmlContent = (this.editorHtmlContent || '') + iframeHtml;
    this.closeVideoDialog();
  }

  private getEmbedUrl(url: string): string | null {
    const YOUTUBE_REGEX = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
    const VIMEO_REGEX = /(?:https?:\/\/)?(?:(?:www\.|player\.)?vimeo\.com\/(?:video\/)?)(\d+)/;
    const ytMatch = url.match(YOUTUBE_REGEX);
    if (ytMatch) {
      return `https://www.youtube.com/embed/${ytMatch[1]}`;
    }
    const vimeoMatch = url.match(VIMEO_REGEX);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    return null;
  }

  imageUploadError = '';

  triggerImageUpload(event: MouseEvent): void {
    event.stopPropagation();
    this.imageUploadError = '';

    // Create the input at document body level so it has zero relationship
    // to the modal DOM tree — no events can bubble through the modal.
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg,image/png,.jpg,.jpeg,.png';
    // Off-screen so it is invisible but still in the DOM long enough to open the OS picker.
    // Appended to document.body (outside the modal tree) so no events can bubble to NgbModal.
    fileInput.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';

    const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png']);

    const cleanup = () => {
      if (document.body.contains(fileInput)) {
        document.body.removeChild(fileInput);
      }
      this._fileDialogOpen = false;
    };

    fileInput.addEventListener('change', (e: Event) => {
      const input = e.target as HTMLInputElement;
      if (input.files && input.files[0]) {
        const file = input.files[0];
        if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
          this.imageUploadError = `"${file.name}" is not supported. Only JPG and PNG images are allowed.`;
          cleanup();
          return;
        }
        if (this.uploadLength !== null && file.size > this.uploadLength) {
          const limitMb = (this.uploadLength / (1024 * 1024)).toFixed(1);
          this.imageUploadError = `"${file.name}" exceeds the maximum allowed image size of ${limitMb} MB.`;
          cleanup();
          return;
        }
        const reader = new FileReader();
        reader.onload = (readerEvent: ProgressEvent<FileReader>) => {
          const dataUrl = readerEvent.target?.result as string;
          if (dataUrl) {
            this.editor.exec('insertImage', { src: dataUrl, alt: file.name });
          }
        };
        reader.readAsDataURL(file);
      }
      cleanup();
    });

    fileInput.addEventListener('cancel', cleanup);

    document.body.appendChild(fileInput);
    this._fileDialogOpen = true;
    fileInput.click();
  }
}
